import type GlobalModel from 'echarts/types/src/model/Global';
import type ExtensionAPI from 'echarts/types/src/core/ExtensionAPI';
import type { Text } from 'zrender';
import { type BarSeriesOption, type EChartsExtensionInstallRegisters } from 'echarts';
import { util } from 'echarts/core';
import BarView from 'echarts/lib/chart/bar/BarView';
import type BarViewType from 'echarts/types/src/chart/bar/BarView';
import { isFunction } from 'zrender/lib/core/util';
import SeriesData, { DefaultDataVisual } from 'echarts/types/src/data/SeriesData';
import BarSeriesModel from 'echarts/types/src/chart/bar/BarSeries';
import Axis2D from 'echarts/types/src/coord/cartesian/Axis2D';
import { overrideIfUndefined } from '../../../util/data';
import {
  FONT_SIZE_LIST,
  THS_FONTFAMILY,
  THS_FONTFAMILY_MEDIUM
} from '../../../theme/mobile/static/font';

type OrderMapping = (dataIndex: number) => number;

/**
 * 代码拷贝自echarts
 * 为了实现动态条形图倒序排序的功能
 */
BarView.prototype._dataSort = function (
  data: SeriesData<BarSeriesModel, DefaultDataVisual>,
  baseAxis: Axis2D,
  orderMapping: OrderMapping
) {
  /** 读自定义配置项 */
  let inverse: boolean;
  if (this.__model.option.dvSortType) {
    inverse = this.__model.option.dvSortType === 'asc';
  } else {
    inverse = this.__model.option.dvSortByAsc;
  }
  /** ************** */
  const info = [];
  data.each(data.mapDimension(baseAxis.dim), function (ordinalNumber, dataIdx) {
    let mappedValue = orderMapping(dataIdx);
    mappedValue = mappedValue === null || mappedValue === undefined ? NaN : mappedValue;
    info.push({
      dataIndex: dataIdx,
      mappedValue,
      ordinalNumber
    });
  });
  info.sort(function (a, b) {
    // If NaN, it will be treated as min val.
    if (Number.isNaN(a.mappedValue)) {
      return inverse ? -1 : 1;
    }
    if (Number.isNaN(b.mappedValue)) {
      return inverse ? 1 : -1;
    }
    // 修改了echarts的排序逻辑，原本只有降序，现在如果配置了升序则改变sort
    return inverse ? a.mappedValue - b.mappedValue : b.mappedValue - a.mappedValue;
  });
  return {
    ordinalNumbers: util.map(info, function (item) {
      return item.ordinalNumber as number;
    })
  };
};

function parseBarItem(seriesOption: BarSeriesOption[]) {
  seriesOption.forEach(option => {
    if (typeof option.label === 'undefined') {
      option.label = {
        fontFamily: THS_FONTFAMILY_MEDIUM,
        fontSize: FONT_SIZE_LIST.seriesLabel
      };
    } else {
      option.label.fontFamily = overrideIfUndefined(option.label.fontFamily, THS_FONTFAMILY);
    }
  });
}

function parseBarLabelCount(ecModel: GlobalModel, api: ExtensionAPI) {
  ecModel.eachSeriesByType('bar', compModel => {
    if (typeof compModel.get('dvMaxLabelCount') === 'undefined') {
      return;
    }

    const barView = api.getViewOfSeriesModel(compModel) as unknown as BarViewType;
    const barItems = barView.group.children().filter(child => child.name === 'item');
    const isShow = barItems.length <= compModel.get('dvMaxLabelCount');

    barItems.forEach(rect => {
      const itemLabel = rect._textContent as Text;
      if (!itemLabel) {
        return;
      }

      itemLabel.stopAnimation(null, true);
      if (isShow) {
        itemLabel.show();
      } else {
        itemLabel.hide();
      }
    });
  });
}

function parseBarBorderRadius(ecModel) {
  ecModel.eachSeriesByType('bar', seriesModel => {
    const itemStyle = seriesModel.get('itemStyle');
    const data = seriesModel.getData();
    if (itemStyle && data.count() > 0 && isFunction(itemStyle?.borderRadius)) {
      data.each(i => {
        const rect = data.getItemGraphicEl(i);
        rect.shape.r = itemStyle.borderRadius(data.getRawDataItem(i));
      });
    }
  });
}

/**
 * 柱状图柱子扩展
 */
export function barItemExtensionInstall(registers: EChartsExtensionInstallRegisters) {
  // * ecModel.setOption() 时
  registers.registerPreprocessor(option => {
    const barSeries = (Array.isArray(option.series) ? option.series : [option.series]).filter(
      item => item.type === 'bar'
    );
    if (barSeries.length === 0) {
      return;
    }

    // @ts-ignore
    parseBarItem(barSeries);
  });

  registers.registerUpdateLifecycle('series:afterupdate', (ecModel, api) => {
    parseBarLabelCount(ecModel, api);
    parseBarBorderRadius(ecModel);
  });
}
